import { http } from "@/lib/http";
import type { FlavorSummary, InstanceMetrics } from "@/features/compute/types";
import type { PrecheckReport } from "@/lib/prechecks";

import type {
  BackupCreateRequest,
  Database,
  DatabaseBackup,
  DatabaseCreateRequest,
  DatabaseInstance,
  DatabaseInstanceCreateRequest,
  DatabaseInstanceUpdateRequest,
  DatabaseReplica,
  DatabaseUser,
  DatabaseUserCreateRequest,
  DatabaseUserUpdateRequest,
  Datastore,
  EngineParameter,
  InstanceResizeRequest,
  MaintenanceState,
  MaintenanceWindow,
  MaintenanceWindowRequest,
  ParameterGroup,
  ParameterGroupCreateRequest,
  ParameterGroupInstance,
  ParameterGroupUpdateRequest,
  PendingChange,
  PendingChangeRequest,
  RootCredentials,
  RootStatus,
  UserAccessGrantRequest,
  VolumeResizeRequest,
} from "./types";

export async function listInstances(): Promise<DatabaseInstance[]> {
  const { data } = await http.get<DatabaseInstance[]>("/database/instances");
  return data;
}

export async function getInstance(
  instanceId: string,
): Promise<DatabaseInstance> {
  const { data } = await http.get<DatabaseInstance>(
    `/database/instances/${instanceId}`,
  );
  return data;
}

export async function createInstance(
  payload: DatabaseInstanceCreateRequest,
): Promise<DatabaseInstance> {
  const { data } = await http.post<DatabaseInstance>(
    "/database/instances",
    payload,
  );
  return data;
}

/** Dry run of a create: every prerequisite and what to fix. Creates nothing. */
export async function precheckInstance(
  payload: DatabaseInstanceCreateRequest,
): Promise<PrecheckReport> {
  const { data } = await http.post<PrecheckReport>(
    "/database/instances/precheck",
    payload,
  );
  return data;
}

export async function updateInstance(
  instanceId: string,
  payload: DatabaseInstanceUpdateRequest,
): Promise<DatabaseInstance> {
  const { data } = await http.patch<DatabaseInstance>(
    `/database/instances/${instanceId}`,
    payload,
  );
  return data;
}

export async function deleteInstance(instanceId: string): Promise<void> {
  await http.delete(`/database/instances/${instanceId}`);
}

export async function restartInstance(instanceId: string): Promise<void> {
  await http.post(`/database/instances/${instanceId}/restart`);
}

export async function resizeInstance(
  instanceId: string,
  payload: InstanceResizeRequest,
): Promise<void> {
  await http.post(`/database/instances/${instanceId}/resize`, payload);
}

export async function resizeVolume(
  instanceId: string,
  payload: VolumeResizeRequest,
): Promise<void> {
  await http.post(`/database/instances/${instanceId}/resize-volume`, payload);
}

export async function getRootStatus(instanceId: string): Promise<RootStatus> {
  const { data } = await http.get<RootStatus>(
    `/database/instances/${instanceId}/root`,
  );
  return data;
}

export async function enableRoot(
  instanceId: string,
): Promise<RootCredentials> {
  const { data } = await http.post<RootCredentials>(
    `/database/instances/${instanceId}/root`,
  );
  return data;
}

export async function disableRoot(instanceId: string): Promise<void> {
  await http.delete(`/database/instances/${instanceId}/root`);
}

export async function listBackups(
  instanceId?: string,
): Promise<DatabaseBackup[]> {
  const { data } = await http.get<DatabaseBackup[]>("/database/backups", {
    params: instanceId ? { instance_id: instanceId } : undefined,
  });
  return data;
}

export async function createBackup(
  payload: BackupCreateRequest,
): Promise<DatabaseBackup> {
  const { data } = await http.post<DatabaseBackup>(
    "/database/backups",
    payload,
  );
  return data;
}

export async function deleteBackup(backupId: string): Promise<void> {
  await http.delete(`/database/backups/${backupId}`);
}

export async function listDatastores(): Promise<Datastore[]> {
  const { data } = await http.get<Datastore[]>("/database/datastores");
  return data;
}

export async function listDatabases(instanceId: string): Promise<Database[]> {
  const { data } = await http.get<Database[]>(
    `/database/instances/${instanceId}/databases`,
  );
  return data;
}

export async function createDatabase(
  instanceId: string,
  payload: DatabaseCreateRequest,
): Promise<Database> {
  const { data } = await http.post<Database>(
    `/database/instances/${instanceId}/databases`,
    payload,
  );
  return data;
}

export async function deleteDatabase(
  instanceId: string,
  name: string,
): Promise<void> {
  await http.delete(
    `/database/instances/${instanceId}/databases/${encodeURIComponent(name)}`,
  );
}

export async function listDbUsers(instanceId: string): Promise<DatabaseUser[]> {
  const { data } = await http.get<DatabaseUser[]>(
    `/database/instances/${instanceId}/users`,
  );
  return data;
}

export async function createDbUser(
  instanceId: string,
  payload: DatabaseUserCreateRequest,
): Promise<DatabaseUser> {
  const { data } = await http.post<DatabaseUser>(
    `/database/instances/${instanceId}/users`,
    payload,
  );
  return data;
}

export async function deleteDbUser(
  instanceId: string,
  name: string,
): Promise<void> {
  await http.delete(
    `/database/instances/${instanceId}/users/${encodeURIComponent(name)}`,
  );
}

export async function updateDbUser(
  instanceId: string,
  name: string,
  payload: DatabaseUserUpdateRequest,
): Promise<void> {
  await http.put(
    `/database/instances/${instanceId}/users/${encodeURIComponent(name)}`,
    payload,
  );
}

export async function grantDbUserAccess(
  instanceId: string,
  name: string,
  payload: UserAccessGrantRequest,
): Promise<void> {
  await http.put(
    `/database/instances/${instanceId}/users/${encodeURIComponent(name)}/databases`,
    payload,
  );
}

export async function revokeDbUserAccess(
  instanceId: string,
  name: string,
  database: string,
): Promise<void> {
  await http.delete(
    `/database/instances/${instanceId}/users/${encodeURIComponent(name)}/databases/${encodeURIComponent(database)}`,
  );
}

/** Time-series for the guest behind a managed database. The guest lives in
 *  the operator's project, so the customer cannot use the instance metrics
 *  route for it; the backend resolves it after the ownership check. */
export async function getInstanceMetrics(
  instanceId: string,
  hours: number,
): Promise<InstanceMetrics> {
  const { data } = await http.get<InstanceMetrics>(
    `/database/instances/${instanceId}/metrics`,
    { params: { hours } },
  );
  return data;
}

/** The sizes a create of this engine version accepts, smallest first (E18):
 *  the cloud can bind sizes to a version and refuses any other at create. */
export async function listDatastoreFlavors(
  datastore: string,
  version: string,
): Promise<FlavorSummary[]> {
  const { data } = await http.get<FlavorSummary[]>(
    `/database/datastores/${encodeURIComponent(datastore)}/versions/${encodeURIComponent(version)}/flavors`,
  );
  return data;
}

// Parameter groups (expose plan EX-3 part A) ------------------------------ //

export async function listEngineParameters(
  datastore: string,
  version: string,
): Promise<EngineParameter[]> {
  const { data } = await http.get<EngineParameter[]>(
    `/database/datastores/${encodeURIComponent(datastore)}/versions/${encodeURIComponent(version)}/parameters`,
  );
  return data;
}

export async function listParameterGroups(): Promise<ParameterGroup[]> {
  const { data } = await http.get<ParameterGroup[]>(
    "/database/parameter-groups",
  );
  return data;
}

export async function getParameterGroup(
  groupId: string,
): Promise<ParameterGroup> {
  const { data } = await http.get<ParameterGroup>(
    `/database/parameter-groups/${groupId}`,
  );
  return data;
}

export async function createParameterGroup(
  payload: ParameterGroupCreateRequest,
): Promise<ParameterGroup> {
  const { data } = await http.post<ParameterGroup>(
    "/database/parameter-groups",
    payload,
  );
  return data;
}

export async function updateParameterGroup(
  groupId: string,
  payload: ParameterGroupUpdateRequest,
): Promise<ParameterGroup> {
  const { data } = await http.put<ParameterGroup>(
    `/database/parameter-groups/${groupId}`,
    payload,
  );
  return data;
}

export async function deleteParameterGroup(groupId: string): Promise<void> {
  await http.delete(`/database/parameter-groups/${groupId}`);
}

export async function listParameterGroupInstances(
  groupId: string,
): Promise<ParameterGroupInstance[]> {
  const { data } = await http.get<ParameterGroupInstance[]>(
    `/database/parameter-groups/${groupId}/instances`,
  );
  return data;
}

export async function attachParameterGroup(
  instanceId: string,
  parameterGroupId: string,
): Promise<DatabaseInstance> {
  const { data } = await http.put<DatabaseInstance>(
    `/database/instances/${instanceId}/parameter-group`,
    { parameter_group_id: parameterGroupId },
  );
  return data;
}

export async function detachParameterGroup(
  instanceId: string,
): Promise<DatabaseInstance> {
  const { data } = await http.delete<DatabaseInstance>(
    `/database/instances/${instanceId}/parameter-group`,
  );
  return data;
}

// Read replicas (expose plan EX-3 part B) -------------------------------- //

export async function listReplicas(
  instanceId: string,
): Promise<DatabaseReplica[]> {
  const { data } = await http.get<DatabaseReplica[]>(
    `/database/instances/${instanceId}/replicas`,
  );
  return data;
}

export async function promoteReplica(instanceId: string): Promise<void> {
  await http.post(`/database/instances/${instanceId}/promote`);
}

export async function detachReplica(instanceId: string): Promise<void> {
  await http.post(`/database/instances/${instanceId}/detach-replica`);
}

// Maintenance windows (docs/build/db-maintenance-windows-design.md) -------- //
export async function getMaintenance(
  instanceId: string,
): Promise<MaintenanceState> {
  const { data } = await http.get<MaintenanceState>(
    `/database/instances/${instanceId}/maintenance`,
  );
  return data;
}

export async function setMaintenanceWindow(
  instanceId: string,
  payload: MaintenanceWindowRequest,
): Promise<MaintenanceWindow> {
  const { data } = await http.put<MaintenanceWindow>(
    `/database/instances/${instanceId}/maintenance/window`,
    payload,
  );
  return data;
}

export async function removeMaintenanceWindow(
  instanceId: string,
): Promise<void> {
  await http.delete(`/database/instances/${instanceId}/maintenance/window`);
}

export async function queueMaintenanceChange(
  instanceId: string,
  payload: PendingChangeRequest,
): Promise<PendingChange> {
  const { data } = await http.post<PendingChange>(
    `/database/instances/${instanceId}/maintenance/changes`,
    payload,
  );
  return data;
}

export async function cancelMaintenanceChange(
  instanceId: string,
  changeId: string,
): Promise<void> {
  await http.delete(`/database/instances/${instanceId}/maintenance/changes/${changeId}`);
}

export async function applyMaintenanceChange(
  instanceId: string,
  changeId: string,
): Promise<MaintenanceState> {
  const { data } = await http.post<MaintenanceState>(
    `/database/instances/${instanceId}/maintenance/changes/${changeId}/apply`,
  );
  return data;
}

export async function postponeMaintenanceWindow(
  instanceId: string,
): Promise<MaintenanceWindow> {
  const { data } = await http.post<MaintenanceWindow>(
    `/database/instances/${instanceId}/maintenance/window/postpone`,
  );
  return data;
}

export async function undoPostponeMaintenanceWindow(
  instanceId: string,
): Promise<void> {
  await http.delete(`/database/instances/${instanceId}/maintenance/window/postpone`);
}
